export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;
export const RAD = 180 / Math.PI;

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function clamp01(v: number): number {
  return clamp(v, 0, 1);
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function invLerp(a: number, b: number, v: number): number {
  return a === b ? 0 : (v - a) / (b - a);
}

export function remap(v: number, inA: number, inB: number, outA: number, outB: number): number {
  return lerp(outA, outB, invLerp(inA, inB, v));
}

/** Hermite smoothstep between edges. */
export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01(invLerp(edge0, edge1, x));
  return t * t * (3 - 2 * t);
}

/** Quintic smootherstep: zero first and second derivative at both edges. */
export function smootherstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01(invLerp(edge0, edge1, x));
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** Wraps an angle into [-PI, PI). */
export function wrapPi(a: number): number {
  let x = (a + Math.PI) % TAU;
  if (x < 0) x += TAU;
  return x - Math.PI;
}

/** Wraps an angle into [0, TAU). */
export function wrapTau(a: number): number {
  let x = a % TAU;
  if (x < 0) x += TAU;
  return x;
}

/**
 * Frame-rate independent exponential approach. `halfLife` is the time in
 * seconds for the remaining error to halve.
 */
export function damp(current: number, target: number, halfLife: number, dt: number): number {
  if (halfLife <= 0) return target;
  const k = Math.pow(0.5, dt / halfLife);
  return target + (current - target) * k;
}

/** Moves `current` toward `target` by at most `maxDelta`. */
export function moveTowards(current: number, target: number, maxDelta: number): number {
  const d = target - current;
  if (Math.abs(d) <= maxDelta) return target;
  return current + Math.sign(d) * maxDelta;
}

/** Signed value with a dead zone removed and the remainder rescaled to full range. */
export function deadzone(v: number, dz: number): number {
  const a = Math.abs(v);
  if (a <= dz) return 0;
  return Math.sign(v) * ((a - dz) / (1 - dz));
}

export function sign01(v: number): number {
  return v >= 0 ? 1 : -1;
}

export function isPow2(v: number): boolean {
  return v > 0 && (v & (v - 1)) === 0;
}

/** Logarithmic interpolation, useful for scales spanning many decades. */
export function logLerp(a: number, b: number, t: number): number {
  return Math.exp(lerp(Math.log(a), Math.log(b), t));
}

/** Formats a distance in metres using the most readable astronomical unit. */
export function formatDistance(metres: number): string {
  const a = Math.abs(metres);
  if (a < 1000) return `${metres.toFixed(1)} m`;
  if (a < 1e7) return `${(metres / 1000).toFixed(2)} km`;
  if (a < 1e10) return `${(metres / 1000).toFixed(0)} km`;
  return `${(metres / 1.495978707e11).toFixed(4)} AU`;
}

export function formatSpeed(mps: number): string {
  const a = Math.abs(mps);
  if (a < 1000) return `${mps.toFixed(1)} m/s`;
  return `${(mps / 1000).toFixed(3)} km/s`;
}

export function formatMass(kg: number): string {
  const a = Math.abs(kg);
  if (a < 1000) return `${kg.toFixed(1)} kg`;
  return `${(kg / 1000).toFixed(2)} t`;
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return '--';
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}
